import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Measure from 'react-measure';
import get from 'lodash/get';
import result from 'lodash/result';
import { Map } from 'immutable';
import VisibilitySensor from 'react-visibility-sensor';

import Quote from 'models/quote';
import Search from 'models/search';
import { Location as LocationModel } from 'models/locations';
import { AddOns as AddOnsModel } from 'models/addons';

import OrderTotal from 'components/common/order-total';
import AmenityList from 'components/common/amenity-list';
import LocationImageGallery from 'components/location/image/gallery';
import LocationDetails from 'components/checkout/additional-information/location-details';

import trackEventCreator from 'action-creators/analytics/track-event';

import { PRICE_CLICK, TOP_PRICE_IMPRESSION } from 'lib/analytics/events';
import { imageCdnAsset } from 'lib/common/url-helpers';

const propTypes = {
  selectedLocation: PropTypes.instanceOf(LocationModel).isRequired,
  currentSearch: PropTypes.instanceOf(Search).isRequired,
  sellers: PropTypes.instanceOf(Map).isRequired,
  quote: PropTypes.instanceOf(Quote),
  checkoutTotal: PropTypes.number,
  selectedAddOns: PropTypes.instanceOf(AddOnsModel),
  userCreditAmount: PropTypes.number,
  couponAmount: PropTypes.number,
  couponCode: PropTypes.string,
  trackEvent: PropTypes.func.isRequired,
};

const defaultProps = {
  checkoutTotal: 0,
  selectedAddOns: new AddOnsModel(),
  userCreditAmount: 0,
  couponAmount: 0,
  couponCode: '',
  quote: new Quote(),
};

class AdditionalInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      listWidth: null,
    };

    this.trackPriceClick = this.trackPriceClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.trackPriceImpression = this.trackPriceImpression.bind(this);
    this.handlePriceVisibilityChange = this.handlePriceVisibilityChange.bind(this);
  }

  lineItemsPresent() {
    const { couponCode, couponAmount, userCreditAmount, selectedAddOns } = this.props;
    return couponCode || parseInt(couponAmount, 10) || parseInt(userCreditAmount, 10) || selectedAddOns.totalCount;
  }

  componentDidUpdate(prevProps) {
    if (get(this.props.quote, 'id', null) !== get(prevProps.quote, 'id', null)) {
      this.trackPriceImpression();
    }
  }

  trackPriceClick(price) {
    this.props.trackEvent({
      ...PRICE_CLICK,
      properties: {
        Price: price,
      },
    });
  }

  onResize(contentRect) {
    if (get(contentRect, 'bounds', false)) {
      this.setState({ dimensions: contentRect.bounds });
    }
  }

  trackPriceImpression() {
    const { trackEvent, quote, checkoutTotal: price } = this.props;
    trackEvent({
      ...TOP_PRICE_IMPRESSION,
      properties: {
        price,
        quote_id: get(quote, 'id', null),
      },
    });
  }

  handlePriceVisibilityChange(visible) {
    if (visible) { this.trackPriceImpression(); }
  }

  renderNextSteps() {
    const { quote } = this.props;

    if (quote.isMonthly) {
      return (
        <div className="padding-horizontal-20 padding-bottom-20 text-size-14 text-color-dark-slate">
          <hr className="ticket-dash" />
          <div className="text-weight-bold margin-bottom-10 margin-top-30">Next Steps</div>
          <div className="text-weight-book margin-bottom-10">After purchasing, you&#39;ll receive a confirmation email for your monthly booking.</div>
          <div className="text-weight-bold">Check the Parking Instructions section of the email for any other steps required before you begin parking.</div>
        </div>
      );
    }

    return (
      <div className="padding-horizontal-20 padding-bottom-20 text-size-14 text-color-dark-slate">
        <hr className="ticket-dash" />
        <div className="text-weight-bold margin-bottom-10 margin-top-30">Next Steps</div>
        <div className="text-weight-book">After purchasing, you&#39;ll receive an email with a link to your parking pass and instructions to park.</div>
      </div>
    );
  }

  render() {
    const {
      selectedLocation,
      quote,
      checkoutTotal,
      sellers,
      currentSearch,
      trackEvent,
    } = this.props;
    const { address1, name, sellerId = '' } = selectedLocation;
    const { validAmenities } = quote;
    const { dimensions } = this.state;
    const seller = sellers.get(sellerId.toString());

    const imageContent = (
      <div>
        <div className="text-align-center text-weight-book text-color-white background-color-dark-slate text-size-16 padding-vertical-20 padding-horizontal-15">
          <div>{address1} - {name}</div>
          <div>{get(seller, 'name', null)}</div>
        </div>
        <LocationImageGallery
          location={selectedLocation}
          currentSearch={currentSearch}
          maxHeight={280}
          width={get(dimensions, 'width', null)}
          app="Checkout"
          trackEvent={trackEvent}
        />
      </div>
    );

    const content = (
      <div className="ticket-style">
        <div className="area ticket-shadow">
          <div className="margin-top-40 margin-bottom-20" onClick={() => this.trackPriceClick(checkoutTotal)}>
            <VisibilitySensor onChange={this.handlePriceVisibilityChange} partialVisibility>
              <OrderTotal
                amount={result(checkoutTotal, 'toString', null)}
                label="ORDER TOTAL"
                location={selectedLocation}
              />
            </VisibilitySensor>
          </div>
        </div>
        <img src={imageCdnAsset('/images/checkout/ticket-indent.png')} alt="" />
        <div className="ticket-shadow">
          <div className="padding-top-30">
            { imageContent }
            <div className="location-detail">
              <AmenityList
                amenities={validAmenities}
                amenityClassName="margin-top-20 col-xs-3"
                className="area amenity-list margin-bottom-30"
              />
              <LocationDetails location={selectedLocation} quote={quote} />
            </div>
          </div>
          { this.renderNextSteps() }
        </div>
      </div>
    );

    return (
      <Measure bounds onResize={this.onResize}>
        {({ measureRef }) => (
          <div ref={measureRef}>{content}</div>
        )}
      </Measure>
    );
  }
}

AdditionalInformation.propTypes = propTypes;
AdditionalInformation.defaultProps = defaultProps;

const mapStateToProps = (state) => {
  const {
    subTotal,
    userCreditAmount,
    couponAmount,
    couponCode,
    selectedAddOns,
  } = state.checkout;

  const {
    locations,
    currentSearch,
    sellers,
  } = state.search;

  return {
    subTotal,
    userCreditAmount,
    couponAmount,
    couponCode,
    selectedAddOns,
    locations,
    currentSearch,
    sellers,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    trackEvent: trackEventCreator,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(AdditionalInformation);
